// Minimal pub/sub bridge so the AI tool executor (which lives in AppLayout,
// far from any list page's filter state) can ask the active properties list to
// apply a filter set. Handles the cross-page race: when the user asks to filter
// from another screen, the model calls navigate('/propiedades') then
// apply_filters; the remount would otherwise miss the emit. takePending captures
// the latest request so a freshly-mounted PropiedadesView consumes it on mount.

export interface FilterRequest {
  view: string;
  filters?: Record<string, unknown>;
  searchQuery?: string;
}

const listeners = new Set<(req: FilterRequest) => void>();
let pending: FilterRequest | null = null;

export function emitFilterRequest(req: FilterRequest): void {
  pending = req;
  listeners.forEach((l) => {
    try {
      l(req);
    } catch {
      // a faulty listener must not break the others
    }
  });
}

export function onFilterRequest(cb: (req: FilterRequest) => void): () => void {
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
  };
}

// Called by a list page on mount to apply a request that was emitted before it
// could subscribe (navigate-then-filter race). Returns null if nothing pending
// for this view.
export function takePendingFilterRequest(view: string): FilterRequest | null {
  if (pending && pending.view === view) {
    const p = pending;
    pending = null;
    return p;
  }
  return null;
}

// Called by a live listener once it has handled a request, so a later
// unmount/remount doesn't reapply a stale filter.
export function clearPendingFilterRequest(): void {
  pending = null;
}

// ── Screen context ───────────────────────────────────────────
// Pages push their state (selected item, active filters, kanban columns)
// so the AI assistant knows what the user is looking at without a tool call.
// Each page owns the full context while mounted and clears on unmount.

export interface ScreenContext {
  page: string;
  selectedProperty?: { id: string; ii_id: string; title: string };
  selectedContact?: { id: string; name: string };
  activeFilters?: Record<string, unknown>;
  kanbanColumns?: Array<{ name: string; count: number }>;
}

let screenContext: ScreenContext | null = null;
const screenListeners = new Set<(ctx: ScreenContext | null) => void>();

export function setScreenContext(ctx: ScreenContext): void {
  screenContext = ctx;
  screenListeners.forEach((fn) => {
    try {
      fn(ctx);
    } catch {
      // a faulty listener must not break the others
    }
  });
}

export function clearScreenContext(): void {
  screenContext = null;
  screenListeners.forEach((fn) => {
    try {
      fn(null);
    } catch {
      // noop
    }
  });
}

export function getScreenContext(): ScreenContext | null {
  return screenContext;
}

export function subscribeScreenContext(cb: (ctx: ScreenContext | null) => void): () => void {
  screenListeners.add(cb);
  return () => {
    screenListeners.delete(cb);
  };
}

// ── Focus appointment ────────────────────────────────────────
// The AI assistant nudge emits a focus request when the user clicks
// "Cita hoy: <title>". CalendarView consumes it on mount / when
// appointments load, finds the matching appointment, and opens the modal.
let pendingFocusAppointmentId: string | null = null;

export function emitFocusAppointment(appointmentId: string): void {
  pendingFocusAppointmentId = appointmentId;
}

export function takePendingFocusAppointment(): string | null {
  const id = pendingFocusAppointmentId;
  pendingFocusAppointmentId = null;
  return id;
}
